import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExpenses } from '../contexts/ExpenseContext';
import { useMode } from '../contexts/ModeContext';
import { 
  CATEGORIES, 
  FAMILY_CATEGORIES,
  TRAVEL_SUBCATEGORIES, 
  TRUCK_SUBCATEGORIES,
  FUEL_SUBCATEGORIES,
  ExpenseCategory, 
  FamilyCategory,
  TravelSubcategory,
  TruckSubcategory,
  FuelSubcategory,
  getLocalDate,
  checkForDuplicates as smartDuplicateCheck,
  formatDuplicateWarning,
  DuplicateCheckResult
} from '../types';
import { 
  Upload as UploadIcon, 
  File, 
  X, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  FileText
} from 'lucide-react';

export default function Upload() {
  const navigate = useNavigate();
  const { addExpense, expenses } = useExpenses();
  const { isFamily } = useMode();
  const visibleCategories = isFamily ? FAMILY_CATEGORIES : CATEGORIES;

  const [files, setFiles] = useState<File[]>([]);
  const [category, setCategory] = useState<ExpenseCategory | FamilyCategory>(isFamily ? 'provisions' : 'travel');
  useEffect(() => {
    if (isFamily && !FAMILY_CATEGORIES.some(c => c.value === category)) {
      setCategory('provisions');
    } else if (!isFamily && !CATEGORIES.some(c => c.value === category)) {
      setCategory('travel');
    }
  }, [isFamily, category]);
  const [travelSubcategory, setTravelSubcategory] = useState<TravelSubcategory>('uber');
  const [truckSubcategory, setTruckSubcategory] = useState<TruckSubcategory>('maintenance');
  const [fuelSubcategory, setFuelSubcategory] = useState<FuelSubcategory>('delivery');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getLocalDate());
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [duplicateConfidence, setDuplicateConfidence] = useState<'high' | 'medium' | 'low' | null>(null);
  const [bypassDuplicateCheck, setBypassDuplicateCheck] = useState(false);
  // Additional fields for smart duplicate detection
  const [vendor, setVendor] = useState('');
  const [storeNumber, setStoreNumber] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [transactionId, setTransactionId] = useState(''); // Tkt#, Invoice#, Receipt#
  const [gallons, setGallons] = useState(''); // Fuel gallons

  // Smart duplicate check using new detection system
  const checkForDuplicates = useCallback((): DuplicateCheckResult | null => {
    if (!amount || !date) return null;
    
    const result = smartDuplicateCheck(
      {
        amount: parseFloat(amount),
        date,
        description,
        vendor,
        storeNumber,
        cardLast4,
        transactionId,
        category,
      },
      expenses
    );
    
    return result.isDuplicate ? result : null;
  }, [expenses, amount, date, description, vendor, storeNumber, cardLast4, category]);
  
  // Get the appropriate subcategory based on category
  const getSubcategory = () => {
    if (category === 'travel') return travelSubcategory;
    if (category === 'truck') return truckSubcategory;
    if (category === 'fuel') return fuelSubcategory;
    return undefined;
  };

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selectedFiles]);
    setError('');
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
    setError('');
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (files.length === 0) {
      setError('Please select at least one file');
      return;
    }

    // Check for duplicates unless bypassed
    if (!bypassDuplicateCheck) {
      const dupeResult = checkForDuplicates();
      if (dupeResult) {
        setDuplicateWarning(formatDuplicateWarning(dupeResult));
        setDuplicateConfidence(dupeResult.confidence);
        return; // Stop and show warning
      }
    }

    setIsUploading(true);
    setError('');
    setDuplicateWarning(null);
    setDuplicateConfidence(null);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        // Build description with gallons if provided
        const fullDescription = gallons 
          ? `${description}${description ? ' - ' : ''}${gallons} gal`
          : description;
        
        await addExpense({
          category,
          subcategory: getSubcategory(),
          description: fullDescription,
          amount: amount ? parseFloat(amount) : undefined,
          date,
          file,
          filename: file.name,
          source: 'manual',
          vendor: vendor || undefined,
          storeNumber: storeNumber || undefined,
          cardLast4: cardLast4 || undefined,
          transactionId: transactionId || undefined,
        });
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      setSuccess(true);
      setBypassDuplicateCheck(false); // Reset for next upload
      setTimeout(() => navigate('/expenses'), 2000);
    } catch (err) {
      setError('Failed to upload files. Please try again.');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto text-center py-20 animate-fade-in">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          {files.length === 1 ? 'Upload Successful!' : `${files.length} Files Uploaded!`}
        </h2>
        <p className="text-white/60">Redirecting to expenses...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Upload Files</h1>
        <p className="text-white/60 mt-1">Add receipts and documents from your device</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="flex items-center space-x-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm animate-fade-in">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Duplicate Warning */}
        {duplicateWarning && (
          <div className={`p-4 rounded-xl animate-fade-in ${
            duplicateConfidence === 'high' 
              ? 'bg-red-500/20 border border-red-500/50' 
              : duplicateConfidence === 'medium'
              ? 'bg-yellow-500/20 border border-yellow-500/50'
              : 'bg-blue-500/20 border border-blue-500/50'
          }`}>
            <div className={`flex items-start space-x-2 text-sm mb-3 ${
              duplicateConfidence === 'high' ? 'text-red-300' :
              duplicateConfidence === 'medium' ? 'text-yellow-300' : 'text-blue-300'
            }`}>
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">
                  {duplicateConfidence === 'high' ? '🚨 Very Likely Duplicate!' :
                   duplicateConfidence === 'medium' ? '⚠️ Probable Duplicate Found' :
                   '❓ Possible Duplicate'}
                </p>
                <p className="mt-2 whitespace-pre-line">{duplicateWarning}</p>
              </div>
            </div>
            <div className="flex gap-3 ml-7">
              <button
                type="button"
                onClick={() => {
                  setBypassDuplicateCheck(true);
                  setDuplicateWarning(null);
                  setDuplicateConfidence(null);
                }}
                className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                  duplicateConfidence === 'high'
                    ? 'bg-red-500/30 text-red-200 hover:bg-red-500/40'
                    : 'bg-yellow-500/30 text-yellow-200 hover:bg-yellow-500/40'
                }`}
              >
                {duplicateConfidence === 'high' ? "I'm Sure - Add Anyway" : 'Add Anyway (Not a Duplicate)'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDuplicateWarning(null);
                  setDuplicateConfidence(null);
                }}
                className="px-4 py-2 bg-white/10 text-white/70 rounded-lg hover:bg-white/20 transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Dropzone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
            files.length > 0
              ? 'border-green-400/50 bg-green-500/10'
              : 'border-white/30 hover:border-white/50 hover:bg-white/5'
          }`}
        >
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
            accept="image/*,.pdf,.doc,.docx"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            {files.length > 0 ? (
              <FileText className="w-12 h-12 text-green-400 mx-auto mb-4" />
            ) : (
              <UploadIcon className="w-12 h-12 text-white/40 mx-auto mb-4" />
            )}
            <p className="text-lg font-medium text-white">
              {files.length > 0 ? 'Tap to add more files' : 'Tap to select files'}
            </p>
            <p className="text-sm text-white/50 mt-1">
              or drag and drop • Images, PDFs, Documents
            </p>
          </label>
        </div>

        {/* Selected Files */}
        {files.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-white/80">
                {files.length} file{files.length !== 1 ? 's' : ''} selected
              </p>
              <button
                type="button"
                onClick={() => setFiles([])}
                className="text-sm text-red-400 hover:text-red-300"
              >
                Clear all
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-2 bg-white/5 rounded-xl p-3">
              {files.map((file, index) => (
                <div 
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between bg-white/10 rounded-lg p-3"
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <File className="w-5 h-5 text-shuttle-sand flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-white text-sm truncate">{file.name}</p>
                      <p className="text-xs text-white/50">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                  >
                    <X className="w-4 h-4 text-white/40" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">
            Category
          </label>
          <div className="grid grid-cols-4 gap-2">
            {visibleCategories.map((cat) => (
              <button
                key={cat.value}
                type="button"
                onClick={() => setCategory(cat.value)}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  category === cat.value
                    ? 'border-shuttle-sand bg-shuttle-sand/20'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <span className="text-xl block">{cat.icon}</span>
                <span className="text-xs text-white/70 mt-1 block truncate">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Travel Subcategory */}
        {category === 'travel' && (
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Travel Type
            </label>
            <div className="grid grid-cols-4 gap-2">
              {TRAVEL_SUBCATEGORIES.map((sub) => (
                <button
                  key={sub.value}
                  type="button"
                  onClick={() => setTravelSubcategory(sub.value)}
                  className={`p-2 rounded-xl border-2 text-center transition-all ${
                    travelSubcategory === sub.value
                      ? 'border-shuttle-sand bg-shuttle-sand/20'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <span className="text-lg block">{sub.icon}</span>
                  <span className="text-xs text-white/70 truncate block">{sub.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Truck Subcategory */}
        {category === 'truck' && (
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Truck Expense Type
            </label>
            <div className="grid grid-cols-5 gap-2">
              {TRUCK_SUBCATEGORIES.map((sub) => (
                <button
                  key={sub.value}
                  type="button"
                  onClick={() => setTruckSubcategory(sub.value)}
                  className={`p-2 rounded-xl border-2 text-center transition-all ${
                    truckSubcategory === sub.value
                      ? 'border-red-400 bg-red-500/20'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <span className="text-lg block">{sub.icon}</span>
                  <span className="text-xs text-white/70 truncate block">{sub.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Fuel Subcategory */}
        {category === 'fuel' && (
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Fuel Type
            </label>
            <div className="grid grid-cols-4 gap-2">
              {FUEL_SUBCATEGORIES.map((sub) => (
                <button
                  key={sub.value}
                  type="button"
                  onClick={() => setFuelSubcategory(sub.value)}
                  className={`p-2 rounded-xl border-2 text-center transition-all ${
                    fuelSubcategory === sub.value
                      ? 'border-orange-400 bg-orange-500/20'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <span className="text-lg block">{sub.icon}</span>
                  <span className="text-xs text-white/70 truncate block">{sub.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-white/80 mb-1">
            Description
          </label>
          <input
            id="description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description..."
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
          />
        </div>

        {/* Amount and Date */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-white/80 mb-1">
              Amount ($)
            </label>
            <input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
            />
          </div>
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-white/80 mb-1">
              Date
            </label>
            <input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
            />
          </div>
        </div>

        {/* Optional: Better Duplicate Detection Fields */}
        <details className="group">
          <summary className="cursor-pointer text-sm text-white/60 hover:text-white/80 transition-colors flex items-center gap-2">
            <span className="text-xs">▶</span>
            <span className="group-open:hidden">🔍 Add details for smarter duplicate detection (optional)</span>
            <span className="hidden group-open:inline">🔍 Hide duplicate detection details</span>
          </summary>
          <div className="mt-3 space-y-3 p-3 bg-white/5 rounded-xl border border-white/10">
            {/* Row 1: Transaction ID (most important) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="transactionId" className="block text-xs font-medium text-yellow-400 mb-1">
                  🎫 Receipt/Tkt # (UNIQUE ID)
                </label>
                <input
                  id="transactionId"
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="3419705"
                  className="w-full px-3 py-2 text-sm bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all"
                />
              </div>
              <div>
                <label htmlFor="gallons" className="block text-xs font-medium text-white/60 mb-1">
                  ⛽ Gallons (fuel only)
                </label>
                <input
                  id="gallons"
                  type="number"
                  step="0.001"
                  value={gallons}
                  onChange={(e) => setGallons(e.target.value)}
                  placeholder="15.929"
                  className="w-full px-3 py-2 text-sm bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
                />
              </div>
            </div>
            
            {/* Row 2: Vendor details */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label htmlFor="vendor" className="block text-xs font-medium text-white/60 mb-1">
                  Vendor
                </label>
                <input
                  id="vendor"
                  type="text"
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  placeholder="Love's, Jiffy Lube..."
                  className="w-full px-3 py-2 text-sm bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
                />
              </div>
              <div>
                <label htmlFor="storeNumber" className="block text-xs font-medium text-white/60 mb-1">
                  Store #
                </label>
                <input
                  id="storeNumber"
                  type="text"
                  value={storeNumber}
                  onChange={(e) => setStoreNumber(e.target.value)}
                  placeholder="674"
                  className="w-full px-3 py-2 text-sm bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
                />
              </div>
              <div>
                <label htmlFor="cardLast4" className="block text-xs font-medium text-white/60 mb-1">
                  Card Last 4
                </label>
                <input
                  id="cardLast4"
                  type="text"
                  maxLength={4}
                  value={cardLast4}
                  onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, ''))}
                  placeholder="8405"
                  className="w-full px-3 py-2 text-sm bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
                />
              </div>
            </div>
            
            <p className="text-xs text-white/40">
              💡 <strong className="text-yellow-400">Receipt/Tkt #</strong> = bulletproof duplicate detection. 
              Same Tkt# = 100% duplicate, even if date/amount differ.
            </p>
          </div>
        </details>

        {/* Upload Progress */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Uploading...</span>
              <span className="font-medium text-shuttle-sand">{uploadProgress}%</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-shuttle-sand to-amber-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isUploading || files.length === 0}
          className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <UploadIcon className="w-5 h-5" />
              <span>
                {files.length === 0 
                  ? 'Select Files' 
                  : files.length === 1 
                  ? 'Upload 1 File' 
                  : `Upload ${files.length} Files`}
              </span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}













